import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, FileImage, Share2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, fmt } from "../lib/api";
import { promoDiscount, itemUnitPrice } from "../lib/pricing";
import { itemName, itemSizeLabel } from "../lib/localize";
import { useCart } from "../context/CartContext";
import { AutoPromoBienvenue } from "../components/AutoPromoBienvenue";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { GoogleRatingBadge } from "../components/GoogleRatingBadge";
import { PromoCodeInput } from "../components/PromoCodeInput";
import { TemoignageDialog } from "../components/TemoignageDialog";
import FreeShippingBar from "../components/FreeShippingBar";
import { ShareButtons } from "../components/ShareButtons";
import { ELink } from "../components/Editable";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

export default function Cart() {
  const { items, remove, updateQty, promo, applyPromo, clearPromo } = useCart();
  const { user } = useAuth();
  const { lang, t, ts } = useLang();
  const navigate = useNavigate();
  const [suggest, setSuggest] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [sharedUrl, setSharedUrl] = useState("");
  const [temoignage, setTemoignage] = useState(false);  // popup témoignage −5 %

  // Panier partageable : instantané côté serveur → lien public /panier-partage/{token}
  const shareCart = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/carts/share", { items });
      setSharedUrl(`${window.location.origin}${data.url}`);
    } catch {
      toast.error(lang === "en" ? "Unable to create the link. Please try again." : "Impossible de créer le lien. Réessayez.");
    } finally {
      setSharing(false);
    }
  };

  // Suggestions « Complétez votre commande » : produits vedettes hors panier
  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } })
      .then((r) => setSuggest((r.data || []).filter((p) => !items.some((i) => i.product_id === p.id)).slice(0, 3)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const total = items.reduce(
    (s, i) => s + itemUnitPrice(i) * i.qty, 0
  );
  const discount = promoDiscount(items, promo);

  const checkout = () => {
    if (!user) navigate("/connexion", { state: { next: "/commande" } });
    else navigate("/commande");
  };

  return (
    <div data-testid="cart-page" className="max-w-5xl mx-auto px-6 lg:px-10 pt-32 pb-28">
      <SEO title={ts("seo.home_title")} description={ts("seo.home_desc")} path="/panier" noindex />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("cart.kicker")}</p>
        <h1 className="font-serif text-5xl md:text-6xl mb-6">{t("cart.title")}</h1>
        <div className="mb-12"><GoogleRatingBadge /></div>
      </FadeUp>

      {items.length === 0 ? (
        <FadeUp delay={0.1} className="text-center py-20 border border-dashed border-ivory/15 rounded-xl">
          <p className="text-ivory/70 mb-6" data-testid="cart-empty">{t("cart.empty")}</p>
          <ELink editPath="link.cart_browse" to="/boutique" data-testid="cart-browse" className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-8 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300">
            {t("cart.browse")} <ArrowRight className="w-4 h-4" />
          </ELink>
        </FadeUp>
      ) : (
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {items.map((i, idx) => {
              const unit = itemUnitPrice(i);   // custom_price admin prioritaire
              return (
                <FadeUp key={i.key} delay={idx * 0.05}>
                  <div data-testid={`cart-item-${idx}`} className="flex gap-5 bg-surface border border-ivory/10 rounded-xl p-5">
                    <img src={i.image} alt={i.name} className="w-24 h-24 object-contain bg-[#f4f2ec] rounded-lg shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-3">
                        <h3 className="font-serif text-xl truncate">{itemName(i, lang)}</h3>
                        <button data-testid={`cart-remove-${idx}`} onClick={() => remove(i.key)} className="text-ivory/65 hover:text-red-400 transition-colors duration-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-ivory/70 text-xs mt-1">{itemSizeLabel(i, lang)} · {t(`cart.${i.inscription_type}`)}</p>
                      {i.inscription_text && <p className="text-champagne text-xs mt-1 italic">« {i.inscription_text} »</p>}
                      {i.color && <p className="text-ivory/60 text-xs mt-1" data-testid={`cart-color-${idx}`}>{t("cart.color")} : {i.color}</p>}
                      {i.note && <p className="text-ivory/60 text-xs mt-1" data-testid={`cart-note-${idx}`}>{t("cart.atelier_note")} : « {i.note} »</p>}
                      {i.logo_name && (
                        <p className="text-ivory/70 text-xs mt-1 flex items-center gap-1.5" data-testid={`cart-logo-${idx}`}>
                          <FileImage className="w-3.5 h-3.5 text-gold" /> {t("cart.logo", { name: i.logo_name })}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-3">
                          <button data-testid={`cart-qty-minus-${idx}`} onClick={() => updateQty(i.key, Math.max(i.pricing?.min_qty || 1, i.qty - 1))} className="w-8 h-8 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors duration-300">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <input data-testid={`cart-qty-input-${idx}`} type="number" min={i.pricing?.min_qty || 1} max="1000" value={i.qty}
                            onChange={(e) => {
                              const v = parseInt(e.target.value);
                              updateQty(i.key, Number.isNaN(v) ? (i.pricing?.min_qty || 1) : Math.max(i.pricing?.min_qty || 1, v));
                            }}
                            className="w-14 text-center font-semibold bg-transparent border-b border-ivory/20 focus:border-gold focus:outline-none text-ivory [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                          <button data-testid={`cart-qty-plus-${idx}`} onClick={() => updateQty(i.key, i.qty + 1)} className="w-8 h-8 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors duration-300">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="text-right">
                          {typeof i.custom_price === "number" && (
                            <p className="text-[10px] uppercase tracking-widest text-gold" data-testid={`cart-custom-price-${idx}`}>
                              {lang === "fr" ? "Prix ajusté pour vous" : "Price adjusted for you"}
                            </p>
                          )}
                          <p className="font-serif text-xl text-champagne">{fmt(unit * i.qty)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>

          <FadeUp delay={0.1}>
            <div className="bg-surface border border-gold/15 rounded-xl p-7 sticky top-24">
              <h3 className="font-serif text-2xl mb-6">{t("cart.summary")}</h3>
              <div className="flex justify-between text-sm text-ivory/60 mb-2">
                <span>{t("cart.items")}</span><span>{items.reduce((s, i) => s + i.qty, 0)}</span>
              </div>
              <div className="flex justify-between text-sm text-ivory/60 mb-5">
                <span>{t("cart.delivery")}</span><span>{t("cart.delivery_note")}</span>
              </div>
              <FreeShippingBar subtotal={Math.max(0, total - discount)} />
              <div className="mb-5">
                <PromoCodeInput promo={promo} onApply={applyPromo} onClear={clearPromo} />
                <AutoPromoBienvenue />
              </div>
              {/* Offre témoignage −5 % (demande du propriétaire, 2026-10-07) :
                  le client raconte son expérience → code unique appliqué au panier.
                  Masquée si un rabais est déjà actif. */}
              {!promo && (
                <button onClick={() => setTemoignage(true)} data-testid="cart-temoignage-offer"
                  className="w-full mb-5 flex items-center gap-3 border border-gold/30 bg-gold/5 rounded-xl px-4 py-3 text-left hover:bg-gold/10 transition-colors duration-300">
                  <Star className="w-5 h-5 text-gold shrink-0" />
                  <span>
                    <span className="block text-sm font-semibold text-gold">
                      {lang === "en" ? "5% off your order" : "−5 % sur votre commande"}
                    </span>
                    <span className="block text-xs text-ivory/55">
                      {lang === "en" ? "Tell us about your experience (2 min)" : "Racontez-nous votre expérience (2 min)"}
                    </span>
                  </span>
                </button>
              )}
              <TemoignageDialog open={temoignage} onClose={() => setTemoignage(false)} lang={lang}
                onCode={(code) => applyPromo({ code, kind: "pct", value: 5, scope: "tout" })} />
              {discount > 0 && (
                <div className="flex justify-between text-sm text-gold mb-2" data-testid="cart-discount-line">
                  <span>{t("promo.discount")} ({promo.code})</span><span>−{fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gold/15 pt-4 mb-7">
                <span className="text-sm uppercase tracking-widest text-ivory/70">{t("cart.subtotal")}</span>
                <span data-testid="cart-total" className="font-serif text-2xl text-gold">{fmt(Math.max(0, total - discount))}</span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={checkout}
                className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-semibold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
              >
                {t("cart.checkout")} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="cart-share-button"
                onClick={shareCart}
                disabled={sharing}
                className="w-full inline-flex items-center justify-center gap-2 mt-3 border border-gold/40 text-gold px-6 py-3 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                {t("shared_cart.share_btn")}
              </button>
              {sharedUrl && (
                <div className="mt-4" data-testid="cart-share-links">
                  <p className="text-xs text-ivory/65 mb-2 text-center">{t("shared_cart.share_ready")}</p>
                  <ShareButtons title={ts("shared_cart.title")} label="" url={sharedUrl} />
                </div>
              )}
              {!user && <p className="text-xs text-ivory/65 mt-4 text-center">{t("cart.account_required")}</p>}
              <p className="text-xs text-ivory/65 mt-4 text-center" data-testid="cart-bulk-note">
                {t("product.bulk_note")}{" "}
                <ELink editPath="link.cart_contact" to="/contact" data-testid="cart-bulk-note-link" className="text-gold hover:text-champagne underline underline-offset-2 transition-colors duration-300">
                  {t("product.bulk_note_link")}
                </ELink>
              </p>
            </div>
          </FadeUp>
        </div>
      )}

      {/* Suggestions complémentaires */}
      {items.length > 0 && suggest.length > 0 && (
        <FadeUp delay={0.15} className="mt-16">
          <h2 className="font-serif text-2xl mb-6" data-testid="cart-suggest-title">{t("cart.complete_title")}</h2>
          <div className="grid grid-cols-3 gap-4">
            {suggest.map((p, i) => (
              <Link key={p.id} to={`/produit/${p.id}`} data-testid={`cart-suggest-${i}`}
                className="group bg-surface border border-ivory/10 rounded-xl overflow-hidden hover:border-gold/50 transition-colors duration-300">
                <div className="bg-white aspect-square flex items-center justify-center p-3">
                  <img src={p.image} alt={itemName(p, lang)} loading="lazy"
                    className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <p className="text-xs text-ivory line-clamp-2 min-h-[2.4em]">{itemName(p, lang)}</p>
                  <p className="text-gold font-serif mt-1">{t("catalogue.grid_from", { price: fmt(p.base_price) })}</p>
                </div>
              </Link>
            ))}
          </div>
        </FadeUp>
      )}
    </div>
  );
}
